import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, X, PhoneCall, DollarSign, Mountain, Truck, Zap, MapPin, ArrowRight, Phone, ChevronDown } from 'lucide-react';
// FAQ Component
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-stone-200 dark:border-white/10 last:border-0" data-id="element-2116">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between py-5 text-left font-bold text-brand-charcoal dark:text-white hover:text-brand-orange transition-colors" data-id="element-2117">
        <span className="text-lg" data-id="element-2118">{question}</span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-300 shrink-0 ml-4 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2119" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} className="overflow-hidden" data-id="element-2120">
        <p className="pb-5 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2121">
          {answer}
        </p>
      </motion.div>
    </div>;
}
export function LocalVsNational() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const comparisonData = [{
    attribute: 'Delivery Speed',
    local: 'Same-day & next-day. Trucks 30 min from your driveway.',
    national: 'Routed to the nearest hauler — could be days out.'
  }, {
    attribute: 'Who Picks Up the Phone',
    local: 'The dispatcher loading your order.',
    national: 'A call center or chatbot that opens a ticket.'
  }, {
    attribute: 'Pricing',
    local: 'Direct from the supplier. No broker markup.',
    national: 'Marketplace fee on top of hauler price.'
  }, {
    attribute: 'Material Expertise',
    local: 'We know what NW contractors actually spec — wet-soil base, drain rock, NW bark.',
    national: 'Generic catalog. No local soil/code knowledge.'
  }, {
    attribute: 'Accountability',
    local: "One number to call if something's wrong. Same crew next time.",
    national: 'Disputes routed through marketplace support.'
  }, {
    attribute: 'Trucks & Drivers',
    local: 'Our fleet, our drivers, every load.',
    national: 'Whichever third-party hauler the system assigns.'
  }, {
    attribute: 'Where the Money Goes',
    local: 'Stays in Pierce & King County.',
    national: 'Often routed out of state — sometimes out of country.'
  }];
  const benefits = [{
    title: 'Talk to the People Loading Your Truck',
    desc: 'No call centers. No ticket queues. When you call us, you speak directly to the dispatchers and drivers handling your order.',
    icon: PhoneCall
  }, {
    title: 'No Marketplace Markup',
    desc: 'National brokers add their cut on top of the material and delivery cost. We are the supplier, so you pay direct pricing.',
    icon: DollarSign
  }, {
    title: 'Local Material Knowledge',
    desc: 'We know Pierce and King County soil. We know what local inspectors look for. We deliver the exact spec you need for the Northwest.',
    icon: Mountain
  }, {
    title: 'Boots on the Ground',
    desc: 'Our fleet, our drivers. You get the same reliable crew load after load, building a relationship that matters on the jobsite.',
    icon: Truck
  }, {
    title: 'Faster Response When Plans Change',
    desc: 'Jobsite delayed? Need an extra 10 yards ASAP? We can pivot in real-time because we control the trucks.',
    icon: Zap
  }, {
    title: 'Your Dollars Stay Local',
    desc: 'We live and work in the 253. When you buy from us, your money stays in the local economy, not a tech hub out of state.',
    icon: MapPin
  }];
  return <div className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen transition-colors duration-300" data-id="element-2122">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-2123">
        <div className="absolute inset-0 z-0" data-id="element-2124">
          <img src="https://cdn.magicpatterns.com/uploads/824wZoPcAij7yeGBiywpRR/file_00000000e00c722f85d5870ff5890903.png" alt="Dump truck delivery" className="w-full h-full object-cover" data-id="element-2125" />
          <div className="absolute inset-0 bg-black/70 dark:bg-black/80" data-id="element-2126" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2127">
          <div className="max-w-3xl" data-id="element-2128">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block py-1.5 px-4 rounded-xl bg-brand-orange/20 text-brand-orange text-sm font-bold tracking-wider uppercase mb-6" data-id="element-2129">
              Local Supplier vs. National Marketplace
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-white mb-6 leading-tight" data-id="element-2130">
              Order Direct. <br data-id="element-2131" />
              <span className="text-brand-orange" data-id="element-2132">Skip the Middleman.</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-stone-300 leading-relaxed mb-8 font-medium font-sub" data-id="element-2133">
              National marketplaces are brokers that route your order to
              whoever's cheapest. We are the local industry expert. When you
              order from 253Gravel, you're talking directly to the people
              loading and delivering your materials.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-2134">
              <Link to="/#materials" className="inline-flex items-center justify-center px-6 py-4 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-xl font-bold text-base sm:text-lg transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2135">
                Get Instant Estimate
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-2136" />
              </Link>
              <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-6 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-xl font-bold text-base sm:text-lg transition-colors" data-id="element-2137">
                <Phone className="w-5 h-5 mr-2" data-id="element-2138" />
                Talk to Dispatch
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2139">
        <div className="text-center mb-16" data-id="element-2140">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2141">
            The Local Advantage
          </h2>
          <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto mb-6" data-id="element-2142"></div>
          <p className="text-lg text-stone-600 dark:text-stone-300 max-w-2xl mx-auto font-sub" data-id="element-2143">
            See exactly why contractors and homeowners choose a direct local
            supplier over national broker platforms.
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-xl overflow-hidden" data-id="element-2144">
          <table className="w-full text-left border-collapse" data-id="element-2145">
            <thead data-id="element-2146">
              <tr className="bg-stone-50 dark:bg-white/5 border-b border-stone-200 dark:border-white/10" data-id="element-2147">
                <th className="p-6 font-bold text-brand-muted w-1/3 text-lg" data-id="element-2148">
                  Attribute
                </th>
                <th className="p-6 font-extrabold text-brand-charcoal dark:text-white w-1/3 border-l border-stone-200 dark:border-white/10 bg-brand-orange/5 dark:bg-brand-orange/10" data-id="element-2149">
                  <div className="flex items-center gap-2" data-id="element-2150">
                    <span className="text-brand-orange" data-id="element-2151">253Gravel</span>
                    <span className="text-sm font-medium text-stone-500 dark:text-stone-400" data-id="element-2152">
                      — Local Supplier
                    </span>
                  </div>
                </th>
                <th className="p-6 font-extrabold text-stone-500 dark:text-stone-400 w-1/3 border-l border-stone-200 dark:border-white/10" data-id="element-2153">
                  National Marketplace
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-2154">
              {comparisonData.map((row, idx) => <tr key={idx} className="hover:bg-stone-50/50 dark:hover:bg-white/[0.02] transition-colors" data-id="element-2155">
                  <td className="p-6 font-bold text-brand-charcoal dark:text-stone-200" data-id="element-2156">
                    {row.attribute}
                  </td>
                  <td className="p-6 border-l border-stone-200 dark:border-white/10 bg-brand-orange/[0.02] dark:bg-brand-orange/[0.05]" data-id="element-2157">
                    <div className="flex items-start gap-3" data-id="element-2158">
                      <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-2159">
                        <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" data-id="element-2160" />
                      </div>
                      <span className="font-medium text-brand-charcoal dark:text-white leading-snug" data-id="element-2161">
                        {row.local}
                      </span>
                    </div>
                  </td>
                  <td className="p-6 border-l border-stone-200 dark:border-white/10" data-id="element-2162">
                    <div className="flex items-start gap-3 opacity-70" data-id="element-2163">
                      <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center" data-id="element-2164">
                        <X className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400" data-id="element-2165" />
                      </div>
                      <span className="font-medium text-stone-600 dark:text-stone-400 leading-snug" data-id="element-2166">
                        {row.national}
                      </span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-6" data-id="element-2167">
          {comparisonData.map((row, idx) => <div key={idx} className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-md overflow-hidden" data-id="element-2168">
              <div className="bg-stone-50 dark:bg-white/5 p-4 border-b border-stone-200 dark:border-white/10" data-id="element-2169">
                <h3 className="font-bold text-brand-charcoal dark:text-white text-lg" data-id="element-2170">
                  {row.attribute}
                </h3>
              </div>
              <div className="p-4 border-b border-stone-200 dark:border-white/10 bg-brand-orange/[0.02] dark:bg-brand-orange/[0.05]" data-id="element-2171">
                <div className="text-xs font-bold text-brand-orange uppercase tracking-wider mb-2" data-id="element-2172">
                  253Gravel — Local Supplier
                </div>
                <div className="flex items-start gap-3" data-id="element-2173">
                  <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-2174">
                    <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" data-id="element-2175" />
                  </div>
                  <span className="font-medium text-brand-charcoal dark:text-white leading-snug" data-id="element-2176">
                    {row.local}
                  </span>
                </div>
              </div>
              <div className="p-4" data-id="element-2177">
                <div className="text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-2178">
                  National Marketplace
                </div>
                <div className="flex items-start gap-3 opacity-70" data-id="element-2179">
                  <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center" data-id="element-2180">
                    <X className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400" data-id="element-2181" />
                  </div>
                  <span className="font-medium text-stone-600 dark:text-stone-400 leading-snug" data-id="element-2182">
                    {row.national}
                  </span>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Pullquote Band */}
      <section className="py-24 bg-brand-charcoal dark:bg-black text-center px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-2183">
        <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/pFhc3AjvRgqipTxvhW5pi7/file_000000003d4c720cb3b00791865cbb87.png')] bg-cover bg-center opacity-10 mix-blend-luminosity" data-id="element-2184" />
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-2185">
          <Mountain className="w-12 h-12 text-brand-orange mx-auto mb-8 opacity-80" data-id="element-2186" />
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-white leading-relaxed mb-8" data-id="element-2187">
            "When you call 253Gravel, you're talking directly to the people
            scheduling and delivering your materials — not a ticket queue
            routing your job to a third-party hauler."
          </h2>
          <div className="w-24 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-2188"></div>
        </div>
      </section>

      {/* Benefit Deep Dive */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2189">
        <div className="text-center mb-16" data-id="element-2190">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2191">
            Why Local Matters
          </h2>
          <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto mb-6" data-id="element-2192"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-2193">
          {benefits.map((benefit, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} className="bg-white dark:bg-[#2a2a2a] p-8 rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm hover:shadow-md transition-shadow" data-id="element-2194">
              <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center mb-6" data-id="element-2195">
                <benefit.icon className="w-6 h-6 text-brand-orange" data-id="element-2196" />
              </div>
              <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3 font-heading" data-id="element-2197">
                {benefit.title}
              </h3>
              <p className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2198">
                {benefit.desc}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-stone-100 dark:bg-[#1b1b1b] border-t border-stone-200 dark:border-white/5" data-id="element-2199">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2200">
          <div className="text-center mb-12" data-id="element-2201">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2202">
              Common Questions
            </h2>
            <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-2203"></div>
          </div>

          <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 p-6 md:p-8 shadow-sm" data-id="element-2204">
            <FaqItem question="Aren't national marketplaces cheaper?" answer="No — they add a marketplace fee on top of the hauler's rate. Because we are the direct supplier and operator, you skip the middleman markup and get honest, direct pricing." data-id="element-2205" />
            <FaqItem question="Can a local supplier handle big commercial jobs?" answer="Absolutely. We deliver anywhere from 5 yards to 100+ yards. We offer automatic bulk discounts for orders of 15+ and 24+ yards, making us highly competitive for large contractor jobs." data-id="element-2206" />
            <FaqItem question="What if I'm outside Pierce or King County?" answer="While our standard zones cover King and Pierce County (as far north as Everett), we regularly do custom-quote runs to surrounding areas. Just call dispatch and we'll get you a rate." data-id="element-2207" />
            <FaqItem question="How fast can you actually deliver?" answer="We offer same-day delivery if ordered before 10 AM (subject to availability), and next-day delivery for almost everything else. Because our trucks are local, we don't have the lag time of a national routing system." data-id="element-2208" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2209">
        <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-6" data-id="element-2210">
          Ready to skip the marketplace?
        </h2>
        <p className="text-xl text-stone-600 dark:text-stone-300 mb-10 max-w-2xl mx-auto font-sub" data-id="element-2211">
          Get your materials from the local industry experts. We're 30 minutes
          from your driveway.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2212">
          <Link to="/#materials" className="inline-flex items-center justify-center px-8 py-4 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-xl font-bold text-lg transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2213">
            Get Instant Estimate
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-2214" />
          </Link>
          <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-8 py-4 bg-stone-200 dark:bg-white/10 hover:bg-stone-300 dark:hover:bg-white/20 text-brand-charcoal dark:text-white rounded-xl font-bold text-lg transition-colors" data-id="element-2215">
            <Phone className="w-5 h-5 mr-2" data-id="element-2216" />
            Call Dispatch
          </a>
        </div>
      </section>
    </div>;
}